import { useSelector } from 'react-redux';
import { storeSidebarLinks, OnlyStoreSidebarLinks, adminSidebarLinks, organizerSidebarLinks, organizationSidebarLinks, ownerTopLinks, getOwnerSidebarLinks } from '../../data/links';
import NavLink from '../ui/NavLink';
import { RootState } from '../../redux/store';
import { sidebarLink } from '../../types/sidebarLink';
import { filterSidebarLinks } from '../../utils/methods/filterSidebarLinksForAdmin';
import { useMemo } from 'react';
import { useSingleStoreAction } from '../../hooks/store/store';
import { filterLinksByStoreMode } from '../../utils/storeMode';
import { useUnreadTicketsCount } from '../../hooks/useUnreadTicketsCount';
import { useLocation } from 'react-router-dom';
import { useIncomingOrdersCount } from '../../hooks/store/orders';


interface SidebarContentProps {
  onLinkClick?: () => void;
  isCollapsed?: boolean;
  isHovered?: boolean;
  isPinned?: boolean;
}
export const SidebarContent: React.FC<SidebarContentProps> = ({ onLinkClick, isCollapsed, isHovered, isPinned }) => {
  const ownerToken = useSelector((s: RootState) => s.owner.accessToken);
  const ownerProvider = useSelector((s: RootState) => s.owner.provider);
  const storeToken = useSelector((s: RootState) => s.store.accessToken);
  const adminToken = useSelector((s: RootState) => s.admin.accessToken);
  const adminPermissions = useSelector((s: RootState) => s.admin.permissions);
  const organizerToken = useSelector((s: RootState) => s.organizer.accessToken);
  const organizationToken = useSelector((s: RootState) => s.organization.accessToken);
  const location = useLocation();
  const { data: currentStore } = useSingleStoreAction(undefined, !!storeToken);
  const shouldLoadUnreadTickets = !!(adminToken || storeToken) && !location.pathname.startsWith('/admin');

  const { data: unreadCount = 0 } = useUnreadTicketsCount(shouldLoadUnreadTickets);
  const { data: incomingOrdersCount = 0 } = useIncomingOrdersCount(currentStore?.id, !!storeToken);

  const filteredAdminLinks = useMemo(() => {
    let links = filterSidebarLinks(adminSidebarLinks, { accessToken: adminToken, permissions: adminPermissions });

    if (adminToken && unreadCount > 0) {
      links = links.map(link => {
        if (link.title === "admin_sidebar.tickets") {
          return { ...link, badge: unreadCount };
        }
        return link;
      });
    }

    return links;
  }, [adminToken, adminPermissions, unreadCount]);

  const filteredStoreLinks = useMemo(() => {
    let links = [...filterLinksByStoreMode(storeSidebarLinks, currentStore?.modes || ["order"], currentStore?.isPosEnabled), ...OnlyStoreSidebarLinks];

    if (storeToken) {
      links = links.map(link => {
        if (link.title === "owner_sidebar.tickets" && unreadCount > 0) {
          return { ...link, badge: unreadCount };
        }
        if (link.title === "owner_sidebar.orders" && incomingOrdersCount > 0) {
          return { ...link, badge: incomingOrdersCount };
        }
        return link;
      });
    }

    return links;
  }, [storeToken, unreadCount, incomingOrdersCount, currentStore?.modes, currentStore?.isPosEnabled]);

  const links: sidebarLink[] = useMemo(() => {
    if (adminToken) {
      return filteredAdminLinks;
    }
    if (ownerToken) {
      return [...ownerTopLinks, ...getOwnerSidebarLinks(ownerProvider)];
    }
    if (storeToken) {
      return filteredStoreLinks;
    }
    if (organizerToken) {
      return [...organizerSidebarLinks];
    }
    if (organizationToken) {
      return [...organizationSidebarLinks];
    }
    return [];
  }, [ownerToken, ownerProvider, storeToken, adminToken, organizerToken, organizationToken, filteredAdminLinks, filteredStoreLinks]);

  return (
    <nav className="flex flex-col gap-1 mt-2">
      {links.map((item) => (
        <div key={item.title}>
          <NavLink link={item} isCollapsed={isCollapsed} isHovered={isHovered} isPinned={isPinned} onLinkClick={onLinkClick} />
        </div>
      ))}
    </nav>
  );
};
